import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Zap, 
  TrendingUp, 
  Users, 
  Clock, 
  AlertTriangle,
  Database,
  HelpCircle,
  Search,
  Wrench,
  Rocket,
  Quote,
  CheckCircle2,
  Sparkles,
  Bot,
  Workflow,
  Handshake,
  UserCheck,
  ArrowUpRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[#0E2A47]" />
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute inset-0 spotlight" />
      
      {/* Data Stream Lines */}
      <div className="absolute left-[10%] top-0 w-px h-full">
        <div className="data-stream-line" />
      </div>
      <div className="absolute left-[30%] top-0 w-px h-full">
        <div className="data-stream-line" style={{ animationDelay: '-5s' }} />
      </div>
      <div className="absolute right-[20%] top-0 w-px h-full">
        <div className="data-stream-line" style={{ animationDelay: '-10s' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <h1 className="text-6xl font-bold mb-4 text-white">Discovify <span className="text-[#E53935]">AI</span></h1>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#0FA4A9]/10 border border-[#0FA4A9]/30 text-[#0FA4A9] font-body text-sm font-medium">
                <Sparkles className="h-4 w-4" />
                AI & Automation Consultancy
              </span>
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Fix What Sucks.
              <br />
              <span className="text-[#0FA4A9]">Automate What Matters.</span>
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="font-body text-lg text-white/70 max-w-xl mb-10"
            >
              We help growing businesses find workflow bottlenecks and fix them with AI and automation so teams get more done with less stress.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button 
                  data-testid="hero-cta-primary"
                  className="bg-[#0FA4A9] hover:bg-[#0FA4A9]/90 text-white font-semibold uppercase tracking-wide px-8 py-6 rounded-sm text-base btn-shine transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(15,164,169,0.6)]"
                >
                  Book a Free Discovery Call
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            
            {/* CTA Clarifying Text */}
            <motion.p 
              variants={fadeInUp}
              className="font-body text-sm text-white/50 mt-4 max-w-md"
            >
              Book a quick call to find out how AI & automation can transform your business.
            </motion.p>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block relative"
          >
            <div className="relative w-full aspect-square max-w-lg mx-auto">
              {/* Animated Workflow Visualization */}
              <div className="absolute inset-0 rounded-sm border border-white/10 bg-[#133456]/30 backdrop-blur-sm overflow-hidden">
                <div className="absolute inset-4">
                  {/* Central Node */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-[#0FA4A9]/20 border-2 border-[#0FA4A9] flex items-center justify-center animate-pulse-glow">
                    <Bot className="h-10 w-10 text-[#0FA4A9]" />
                  </div>
                  
                  {/* Orbiting Nodes */}
                  <div className="absolute top-8 left-1/2 -translate-x-1/2 w-16 h-16 rounded-sm bg-[#1E4B75] border border-white/20 flex items-center justify-center animate-float">
                    <Workflow className="h-6 w-6 text-white/60" />
                  </div>
                  <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-16 h-16 rounded-sm bg-[#1E4B75] border border-white/20 flex items-center justify-center animate-float" style={{ animationDelay: '-2s' }}>
                    <Database className="h-6 w-6 text-white/60" />
                  </div>
                  <div className="absolute top-1/2 left-8 -translate-y-1/2 w-16 h-16 rounded-sm bg-[#1E4B75] border border-white/20 flex items-center justify-center animate-float" style={{ animationDelay: '-3s' }}>
                    <Users className="h-6 w-6 text-white/60" />
                  </div>
                  <div className="absolute top-1/2 right-8 -translate-y-1/2 w-16 h-16 rounded-sm bg-[#1E4B75] border border-white/20 flex items-center justify-center animate-float" style={{ animationDelay: '-4s' }}>
                    <Zap className="h-6 w-6 text-white/60" />
                  </div>
                  
                  {/* Connection Lines */}
                  <svg className="absolute inset-0 w-full h-full" style={{ zIndex: -1 }}>
                    <line x1="50%" y1="15%" x2="50%" y2="35%" stroke="rgba(15, 164, 169, 0.3)" strokeWidth="1" strokeDasharray="4 4" />
                    <line x1="50%" y1="65%" x2="50%" y2="85%" stroke="rgba(15, 164, 169, 0.3)" strokeWidth="1" strokeDasharray="4 4" />
                    <line x1="15%" y1="50%" x2="35%" y2="50%" stroke="rgba(15, 164, 169, 0.3)" strokeWidth="1" strokeDasharray="4 4" />
                    <line x1="65%" y1="50%" x2="85%" y2="50%" stroke="rgba(15, 164, 169, 0.3)" strokeWidth="1" strokeDasharray="4 4" />
                  </svg>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Partner Credibility Section
const PartnerSection = () => {
  const SUBBLY_LOGO = "https://customer-assets.emergentagent.com/job_fix-workflows/artifacts/1tqq1otv_subbly%20logo.png";
  const JOBNIMBUS_LOGO = "https://customer-assets.emergentagent.com/job_fix-workflows/artifacts/xpkbicqr_Job%20Nimbus%20Logo.png";

  return (
    <section className="py-16 bg-[#0A1F35] border-t border-white/5" data-testid="partner-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2"
          >
            Official AI & Automation Partner Of
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto"
        >
          {/* Subbly Partner */}
          <motion.div
            variants={fadeInUp}
            className="glass-card rounded-sm p-8 hover:border-[#0FA4A9]/30 transition-colors"
          >
            <div className="flex flex-col items-center text-center">
              <div className="h-20 mb-6 flex items-center justify-center bg-white rounded-sm px-6 py-3">
                <img 
                  src={SUBBLY_LOGO} 
                  alt="Subbly" 
                  className="h-12 w-auto object-contain"
                />
              </div>
              <p className="font-body text-white/70 text-sm mb-4">
                Helping subscription businesses streamline customer lifecycle operations, automate workflows, and build smarter processes with AI.
              </p>
              <a 
                href="https://partners.discovify.ai/subbly"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center font-body text-sm text-[#0FA4A9]/70 hover:text-[#0FA4A9] transition-colors"
                data-testid="partner-subbly-link"
              >
                Learn more
                <ArrowUpRight className="ml-1 h-3 w-3" />
              </a>
            </div>
          </motion.div>

          {/* JobNimbus Partner */}
          <motion.div
            variants={fadeInUp}
            className="glass-card rounded-sm p-8 hover:border-[#0FA4A9]/30 transition-colors"
          >
            <div className="flex flex-col items-center text-center">
              <div className="h-20 mb-6 flex items-center justify-center bg-white rounded-sm px-6 py-3">
                <img 
                  src={JOBNIMBUS_LOGO} 
                  alt="JobNimbus" 
                  className="h-12 w-auto object-contain"
                />
              </div>
              <p className="font-body text-white/70 text-sm mb-4">
                Supporting contractors and service businesses with AI-powered lead handling, communication workflows, and operational efficiency.
              </p>
              <a 
                href="https://partners.discovify.ai/jobnimbus-partner"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center font-body text-sm text-[#0FA4A9]/70 hover:text-[#0FA4A9] transition-colors"
                data-testid="partner-jobnimbus-link"
              >
                Learn more
                <ArrowUpRight className="ml-1 h-3 w-3" />
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Problem/Solution Section
const ProblemSolutionSection = () => {
  const problems = [
    {
      icon: Clock,
      problem: "Manual busywork eating hours",
      solution: "Automated task flows that handle repetitive work while you sleep"
    },
    {
      icon: AlertTriangle,
      problem: "Leads slipping through cracks",
      solution: "AI-powered lead follow-up that never forgets to respond"
    },
    {
      icon: Database,
      problem: "Data scattered across tools",
      solution: "Unified dashboards and automated data sync between platforms"
    },
    {
      icon: HelpCircle,
      problem: "Teams confused about how to use AI",
      solution: "Practical AI playbooks and hands-on team training"
    }
  ];

  return (
    <section className="py-24 bg-[#0A1F35]" data-testid="problem-solution-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            Your Business is <span className="text-[#E53935]">Leaking</span> Time & Money
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 max-w-2xl mx-auto"
          >
            We find where the leaks are and patch them with smart automation
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-6"
        >
          {problems.map((item, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="glass-card rounded-sm p-8 hover:border-[#0FA4A9]/50 transition-colors duration-300 tracing-beam"
            >
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-[#E53935]/10 flex items-center justify-center">
                  <item.icon className="h-6 w-6 text-[#E53935]" />
                </div>
                <div className="flex-grow">
                  <p className="font-body text-white/50 text-sm mb-2 line-through decoration-[#E53935]/50">
                    {item.problem}
                  </p>
                  <div className="flex items-center gap-2 mb-2">
                    <ArrowRight className="h-4 w-4 text-[#0FA4A9]" />
                    <CheckCircle2 className="h-4 w-4 text-[#0FA4A9]" />
                  </div>
                  <p className="font-body text-white font-medium">
                    {item.solution}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Zap,
      title: "AI Consulting",
      description: "We identify bottlenecks, implement AI solutions, and build automations that save time and win more customers.",
      link: "/ai-consulting",
      accent: "#0FA4A9"
    },
    {
      icon: Handshake,
      title: "Partner With Us",
      description: "We partner with software platforms and service providers to help their customers implement AI & automation—adding value, improving retention, and extending LTV.",
      link: "/contact",
      accent: "#0FA4A9"
    }
  ];

  return (
    <section className="py-24 bg-[#0E2A47]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            How We Help
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 max-w-2xl mx-auto"
          >
            Two paths to AI-powered efficiency
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              whileHover={{ y: -8 }}
              className="glass-card rounded-sm p-8 hover:border-[#0FA4A9]/50 transition-all duration-300 group"
            >
              <div className="w-14 h-14 rounded-sm bg-[#0FA4A9]/10 flex items-center justify-center mb-6 group-hover:bg-[#0FA4A9]/20 transition-colors">
                <service.icon className="h-7 w-7 text-[#0FA4A9]" />
              </div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">
                {service.title}
              </h3>
              <p className="font-body text-white/60 mb-6">
                {service.description}
              </p>
              <Link 
                to={service.link}
                className="inline-flex items-center font-body font-medium text-[#0FA4A9] hover:text-[#0FA4A9]/80 transition-colors"
                data-testid={`service-link-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                Learn More
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Engagement Options Section (Done With You / Done For You)
const EngagementSection = () => {
  const options = [
    {
      icon: Handshake,
      title: "Done With You",
      description: "We guide you through the process while you maintain control.",
      forWho: "Best for teams who want to learn and build internal AI capabilities.",
      howItWorks: "We assess your workflows, create a roadmap, and coach your team through implementation with regular check-ins.",
      result: "Your team gains AI skills and owns the systems long-term."
    },
    {
      icon: UserCheck,
      title: "Done For You",
      description: "We handle everything from assessment to deployment.",
      forWho: "Best for busy teams who want results without the learning curve.",
      howItWorks: "We audit your operations, build the automations, integrate with your tools, and train your team on usage.",
      result: "Turnkey AI solutions running in your business with minimal effort from your side."
    }
  ];

  return (
    <section className="py-24 bg-[#0A1F35]" data-testid="engagement-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            Two Ways to Work Together
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 max-w-2xl mx-auto"
          >
            Choose the level of support that fits your team
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-8"
        >
          {options.map((option, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="glass-card rounded-sm p-8 hover:border-[#0FA4A9]/50 transition-colors"
            >
              <div className="w-14 h-14 rounded-sm bg-[#0FA4A9]/10 flex items-center justify-center mb-6">
                <option.icon className="h-7 w-7 text-[#0FA4A9]" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-3">
                {option.title}
              </h3>
              <p className="font-body text-white/70 mb-6">
                {option.description}
              </p>
              
              <div className="space-y-4">
                <div>
                  <p className="font-body text-xs uppercase tracking-wider text-[#0FA4A9] mb-1">Who it's for</p>
                  <p className="font-body text-white/60 text-sm">{option.forWho}</p>
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-wider text-[#0FA4A9] mb-1">How it works</p>
                  <p className="font-body text-white/60 text-sm">{option.howItWorks}</p>
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-wider text-[#0FA4A9] mb-1">What you get</p>
                  <p className="font-body text-white/60 text-sm">{option.result}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      icon: Search,
      title: "Discover",
      description: "We run a discovery session to find where your workflows are breaking and where AI can help."
    },
    {
      number: "02",
      icon: Wrench,
      title: "Design",
      description: "We map and build your automations and AI culture playbook tailored to your team."
    },
    {
      number: "03",
      icon: Rocket,
      title: "Deploy",
      description: "We launch, integrate, and train your team to use AI confidently every day."
    }
  ];

  return (
    <section className="py-24 bg-[#0A1F35]" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            Our Process
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 max-w-2xl mx-auto"
          >
            Simple, effective, no-nonsense approach to automation
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="relative"
            >
              {/* Connection Line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-20 left-1/2 w-full h-px bg-gradient-to-r from-[#0FA4A9]/50 to-transparent" />
              )}
              
              <div className="relative z-10 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0FA4A9]/10 border-2 border-[#0FA4A9] mb-6">
                  <step.icon className="h-7 w-7 text-[#0FA4A9]" />
                </div>
                <div className="font-heading text-5xl font-bold text-[#0FA4A9]/20 mb-2">
                  {step.number}
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">
                  {step.title}
                </h3>
                <p className="font-body text-white/60">
                  {step.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "I had a great conversation with B.J. about how to integrate AI into my tasks to streamline the tasks and processes that bog down my week. His presentation at the CHQ Chamber workshop was engaging and took this same approach, of identifying pinch points in your business or workflow and how AI may help, in an approachable way.",
      name: "Daniel Heitzenrater",
      role: "President & CEO",
      company: "Chautauqua County Chamber of Commerce"
    },
    {
      quote: "B.J. has been a pleasure to work with, and his innovative approach to AI has allowed us to identify opportunities for growth within our business. Without his tooling, we would have taken much longer to identify and address those opportunities and by that time, we may have been too late. If you're looking to take advantage of AI, but don't know where to start, start with B.J.!",
      name: "Dominick Zangaro",
      role: "CEO",
      company: "Miti"
    },
    {
      quote: "We are very impressed with the AI voice agent B.J. implemented for us, and how he built out our auto AI responses to customers within our CRM system. He hit the nail on the head with every aspect, especially helping us not miss customer inquiries. We are a busy roofing company so it's easy to miss and not capture customers when they reach out. We're very impressed and look forward to continuing to work with B.J. and his AI services!",
      name: "Nick Bennett",
      role: "Owner",
      company: "EB Builders - Jamestown, NY"
    }
  ];

  return (
    <section className="py-24 bg-[#0E2A47] overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4"
          >
            What Clients Say
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60"
          >
            Real results from real businesses
          </motion.p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="flex gap-6 animate-marquee"
        style={{ width: 'max-content' }}
      >
        {[...testimonials, ...testimonials].map((testimonial, index) => (
          <div
            key={index}
            className="w-[400px] flex-shrink-0 glass-card rounded-sm p-8"
          >
            <Quote className="h-8 w-8 text-[#0FA4A9]/50 mb-4" />
            <p className="font-body text-white/80 text-lg mb-6">
              "{testimonial.quote}"
            </p>
            <div>
              <p className="font-body font-semibold text-white">
                {testimonial.name}
              </p>
              <p className="font-body text-sm text-white/50">
                {testimonial.role}, {testimonial.company}
              </p>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Community Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={fadeInUp}
          className="text-center"
        >
          <p className="font-body text-sm uppercase tracking-wider text-white/40 mb-6">
            Proudly part of
          </p>
          <div className="flex justify-center items-center gap-12 flex-wrap">
            <div className="px-6 py-3 rounded-sm bg-white/5 border border-white/10">
              <span className="font-heading text-lg font-bold text-white/60">Playmakers AI</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  return (
    <section className="py-24 bg-[#0E2A47] relative overflow-hidden" data-testid="final-cta-section">
      <div className="absolute inset-0 spotlight" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2 
            variants={fadeInUp}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            Ready to Fix What's Broken?
          </motion.h2>
          <motion.p 
            variants={fadeInUp}
            className="font-body text-lg text-white/60 mb-10 max-w-2xl mx-auto"
          >
            Book a free discovery call and let's find where your business is leaking time and money.
          </motion.p>
          <motion.div variants={fadeInUp}>
            <Link to="/contact">
              <Button 
                data-testid="final-cta-btn"
                className="bg-[#E53935] hover:bg-[#E53935]/90 text-white font-bold uppercase tracking-widest px-10 py-6 rounded-sm text-lg btn-shine transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(229,57,53,0.6)]"
              >
                Let's Talk
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <p className="font-body text-sm text-white/50 mt-4">
              We'll help determine which AI assessment is the right fit for your business.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <PartnerSection />
      <ProblemSolutionSection />
      <ServicesSection />
      <EngagementSection />
      <ProcessSection />
      <TestimonialsSection />
      <FinalCTASection />
    </div>
  );
}
